import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NoteReview, NoteReviewStateInterface } from './interfaces';
import { OverallReviewResponse, StateSliceStatus } from '@ax/constants';
import { selectedVisitSlice } from '../selectedVisit';

export const noteReviewInitialState: NoteReviewStateInterface = {
  status: StateSliceStatus.Uninitialized,
  showPoorReviewModal: false,
};

export const noteReviewSlice = createSlice({
  name: 'review',
  initialState: noteReviewInitialState,
  extraReducers: (builder) => {
    builder
      .addCase(selectedVisitSlice.actions.setSelectedVisitDetails, (state, action) => {
        if (!action.payload.uuid || state.noteId === action.payload.uuid) {
          return {
            ...state,
          };
        }

        return {
          ...noteReviewInitialState,
          noteId: action.payload.uuid,
        };
      })
      .addCase(selectedVisitSlice.actions.clearVisitDetails, () => {
        return {
          ...noteReviewInitialState,
        };
      });
  },
  reducers: {
    noteOverallReviewSubmitted(
      state,
      action: PayloadAction<{ overallReview: OverallReviewResponse; noteId: string }>,
    ) {
      return {
        ...state,
        noteId: action.payload.noteId,
        status: StateSliceStatus.Loading,
      };
    },
    noteOverallReviewSubmissionFailed(
      state,
      action: PayloadAction<{
        overallReview: OverallReviewResponse;
        noteId: string;
        errorMessage?: string;
      }>,
    ) {
      return {
        ...state,
        noteId: action.payload.noteId,
        status: StateSliceStatus.Error,
        errorMessage: action.payload.errorMessage,
      };
    },
    noteOverallReviewSubmissionSucceeded(state, action: PayloadAction<{ review: NoteReview }>) {
      return {
        ...state,
        review: action.payload.review,
        status: StateSliceStatus.Initialized,
        showPoorReviewModal:
          action.payload.review.overallReview === OverallReviewResponse.THUMBS_DOWN,
      };
    },
    poorReviewResponseSubmitted(state, _: PayloadAction<{ review: NoteReview }>) {
      return {
        ...state,
        status: StateSliceStatus.Loading,
      };
    },
    poorReviewResponseSubmissionFailed(
      state,
      action: PayloadAction<{
        review: NoteReview;
        errorMessage?: string;
      }>,
    ) {
      return {
        ...state,
        status: StateSliceStatus.Error,
        errorMessage: action.payload.errorMessage,
      };
    },
    poorReviewResponseSubmissionSucceeded(state, action: PayloadAction<{ review: NoteReview }>) {
      return {
        ...state,
        review: action.payload.review,
        status: StateSliceStatus.Initialized,
        showPoorReviewModal: false,
      };
    },
    loadNoteReviewRequested(state, action: PayloadAction<{ noteId: string }>) {
      return {
        ...state,
        noteId: action.payload.noteId,
        status: StateSliceStatus.Loading,
      };
    },
    loadNoteReviewSucceeded(state, action: PayloadAction<{ review: NoteReview }>) {
      return {
        ...state,
        review: {
          ...action.payload.review,
        },
        status: StateSliceStatus.Initialized,
      };
    },
    loadNoteReviewFailed(state, action: PayloadAction<{ errorMessage?: string }>) {
      return {
        ...state,
        errorMessage: action.payload.errorMessage,
        status: StateSliceStatus.Error,
      };
    },
    poorReviewModalClosed(state, _: PayloadAction<{ noteId: string }>) {
      return {
        ...state,
        showPoorReviewModal: false,
      };
    },
  },
});

export const {
  noteOverallReviewSubmitted,
  noteOverallReviewSubmissionFailed,
  noteOverallReviewSubmissionSucceeded,
  poorReviewResponseSubmitted,
  poorReviewResponseSubmissionFailed,
  poorReviewResponseSubmissionSucceeded,
  loadNoteReviewRequested,
  loadNoteReviewSucceeded,
  loadNoteReviewFailed,
  poorReviewModalClosed,
} = noteReviewSlice.actions;

export default noteReviewSlice.reducer;
